import { useState, useEffect, useCallback, useRef } from 'react';
import { suppliersAPI, supplierReconAPI } from '../lib/api';
import {
  FiUsers, FiSearch, FiPlus, FiEdit2, FiTrash2, FiTag, FiX,
  FiLoader, FiChevronDown, FiChevronUp, FiAlertCircle, FiCheck,
  FiMapPin, FiPhone, FiMail, FiDollarSign, FiHash, FiUpload,
} from 'react-icons/fi';

// ─── SupplierModal ────────────────────────────────────────────────────────────

function SupplierModal({ supplier, groups, onSave, onClose }) {
  const isEdit = !!supplier?.id;
  const [form, setForm] = useState({
    group_id: supplier?.group_id || (groups[0]?.id || ''),
    canonical_name: supplier?.canonical_name || '',
    supplier_code: supplier?.supplier_code || '',
    vat_number: supplier?.vat_number || '',
    bank_name: supplier?.bank_name || '',
    bank_account: supplier?.bank_account || '',
    bank_branch: supplier?.bank_branch || '',
    address: supplier?.address || '',
    contact_email: supplier?.contact_email || '',
    contact_phone: supplier?.contact_phone || '',
    notes: supplier?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.canonical_name.trim()) return setError('Supplier name is required');
    if (!form.group_id) return setError('Recon group is required');
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await suppliersAPI.update(supplier.id, form);
      } else {
        await suppliersAPI.create(form);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 text-primary-700">
            <FiUsers className="w-5 h-5" />
            <h2 className="text-base font-semibold">{isEdit ? 'Edit Supplier' : 'Add Supplier'}</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Recon Group *</label>
              <select
                value={form.group_id}
                onChange={set('group_id')}
                disabled={isEdit}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
              >
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Supplier Name (Canonical) *</label>
              <input
                type="text"
                value={form.canonical_name}
                onChange={set('canonical_name')}
                placeholder="e.g. Distri Liq"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Supplier Code</label>
              <input
                type="text"
                value={form.supplier_code}
                onChange={set('supplier_code')}
                placeholder="e.g. GER002"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Banking Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Bank Name</label>
                <input type="text" value={form.bank_name} onChange={set('bank_name')} placeholder="e.g. ABSA" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Account Number</label>
                <input type="text" value={form.bank_account} onChange={set('bank_account')} placeholder="Account #" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Branch Code</label>
                <input type="text" value={form.bank_branch} onChange={set('bank_branch')} placeholder="Branch code" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact & Tax</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">VAT Number</label>
                <input type="text" value={form.vat_number} onChange={set('vat_number')} placeholder="VAT reg number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                <input type="text" value={form.contact_phone} onChange={set('contact_phone')} placeholder="Contact number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input type="email" value={form.contact_email} onChange={set('contact_email')} placeholder="Contact email" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
                <input type="text" value={form.address} onChange={set('address')} placeholder="Physical address" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={set('notes')}
              placeholder="Internal notes about this supplier..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
            {isEdit ? 'Save Changes' : 'Add Supplier'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ImportModal ─────────────────────────────────────────────────────────────

const CSV_COLUMNS = ['supplier_code', 'name', 'bank_name', 'bank_account', 'bank_branch', 'vat_number'];
const CSV_HEADERS = ['Supplier Code', 'Name *', 'Bank Name', 'Bank Account No', 'Bank Branch Code', 'Tax Number'];

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
  const rows = lines.slice(1).map(line => {
    const cells = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === ',' && !inQ) { cells.push(cur.trim()); cur = ''; }
      else { cur += line[i]; }
    }
    cells.push(cur.trim());
    return cells;
  });
  return { headers, rows };
}

function autoMap(headers) {
  const map = {};
  const norm = h => h.toLowerCase().replace(/[^a-z0-9]/g, '');
  const candidates = {
    supplier_code: ['suppliercode', 'code', 'supplier'],
    name: ['name', 'suppliername', 'canonicalname', 'supplier'],
    bank_name: ['bankname', 'bank'],
    bank_account: ['bankaccount', 'accountnumber', 'accountno', 'bankaccountno'],
    bank_branch: ['bankbranch', 'branchcode', 'bankbranchcode'],
    vat_number: ['vatnumber', 'vat', 'taxnumber', 'tax'],
  };
  for (const [field, aliases] of Object.entries(candidates)) {
    const idx = headers.findIndex(h => aliases.includes(norm(h)));
    if (idx >= 0) map[field] = idx;
  }
  return map;
}

function ImportModal({ groups, onClose, onDone }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | map | preview | done
  const [groupId, setGroupId] = useState(groups[0]?.id || '');
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [colMap, setColMap] = useState({});
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { done, total }
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);

  const BATCH_SIZE = 500;

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers, rows } = parseCSV(ev.target.result);
      setRawHeaders(headers);
      setRawRows(rows);
      setColMap(autoMap(headers));
      setStep('map');
    };
    reader.readAsText(file);
  };

  const buildRows = () => rawRows.map(cells => {
    const r = {};
    for (const [field, idx] of Object.entries(colMap)) {
      r[field] = (cells[idx] ?? '').trim();
    }
    return r;
  }).filter(r => r.name);

  const runBatched = async (rows, dryRun) => {
    const allResults = [];
    for (let i = 0; i < rows.length; i += BATCH_SIZE) {
      setImportProgress({ done: i, total: rows.length });
      const batch = rows.slice(i, i + BATCH_SIZE);
      const res = await suppliersAPI.import(groupId, batch, dryRun);
      allResults.push(...res.data.results);
    }
    setImportProgress({ done: rows.length, total: rows.length });
    const summary = allResults.reduce((s, r) => { s[r.action] = (s[r.action] || 0) + 1; return s; }, {});
    return { summary, results: allResults, dry_run: dryRun };
  };

  const handlePreview = () => {
    // Client-side preview only — no server call needed, avoids timeout on large files
    const rows = buildRows();
    const withCode = rows.filter(r => r.supplier_code).length;
    const withoutCode = rows.length - withCode;
    setPreview({
      dry_run: true,
      client_side: true,
      total: rows.length,
      with_code: withCode,
      without_code: withoutCode,
      sample: rows.slice(0, 8),
    });
    setStep('preview');
  };

  const handleImport = async () => {
    setError(null);
    setImporting(true);
    setImportProgress(null);
    try {
      const data = await runBatched(buildRows(), false);
      setImportResult(data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const actionColor = (action) => ({
    updated: 'text-blue-700 bg-blue-50',
    inserted: 'text-green-700 bg-green-50',
    insert: 'text-green-700 bg-green-50',
    skipped: 'text-gray-500 bg-gray-50',
    error: 'text-red-700 bg-red-50',
  }[action] || 'text-gray-600 bg-gray-50');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 text-primary-700">
            <FiUpload className="w-5 h-5" />
            <h2 className="text-base font-semibold">Import Suppliers</h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><FiX className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <FiAlertCircle className="w-4 h-4 flex-shrink-0" />{error}
            </div>
          )}

          {/* Step: upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Recon Group *</label>
                <select value={groupId} onChange={e => setGroupId(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary-500">
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-3">
                  Upload a CSV file with supplier data. Expected columns (order does not matter):
                </p>
                <div className="flex flex-wrap gap-1 mb-4">
                  {CSV_HEADERS.map((h, i) => (
                    <span key={i} className={`text-xs px-2 py-0.5 rounded font-mono ${CSV_COLUMNS[i] === 'name' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'}`}>{h}</span>
                  ))}
                </div>
                <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" />
                <button onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors">
                  <FiUpload className="w-4 h-4" /> Choose CSV file
                </button>
              </div>
            </div>
          )}

          {/* Step: map columns */}
          {step === 'map' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Recon Group *</label>
                <select value={groupId} onChange={e => setGroupId(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:ring-2 focus:ring-primary-500">
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <p className="text-xs text-gray-500">{rawRows.length} rows found. Map your CSV columns to supplier fields:</p>
              <div className="grid grid-cols-2 gap-3">
                {CSV_COLUMNS.map((field, i) => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {CSV_HEADERS[i]}{field === 'name' ? '' : ''}
                    </label>
                    <select
                      value={colMap[field] ?? ''}
                      onChange={e => setColMap(prev => ({ ...prev, [field]: e.target.value === '' ? undefined : Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">— not mapped —</option>
                      {rawHeaders.map((h, idx) => <option key={idx} value={idx}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {importProgress && (
                <div className="space-y-1 pt-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Analysing…</span>
                    <span>{importProgress.done} / {importProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-primary-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.round(importProgress.done / importProgress.total * 100)}%` }} />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setStep('upload')} disabled={importing} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">Back</button>
                <button
                  onClick={handlePreview}
                  disabled={importing || colMap.name == null}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {importing ? <FiLoader className="w-4 h-4 animate-spin" /> : null}
                  Preview ({buildRows().length} rows)
                </button>
              </div>
            </div>
          )}

          {/* Step: preview */}
          {step === 'preview' && preview && (
            <div className="space-y-4">
              {preview.client_side ? (
                <>
                  <p className="text-sm text-gray-600">Ready to import <span className="font-semibold">{preview.total} suppliers</span> into the selected group.</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="border border-gray-200 rounded-lg px-4 py-3">
                      <p className="text-xs text-gray-400 mb-1">With supplier code</p>
                      <p className="text-xl font-semibold text-gray-900">{preview.with_code}</p>
                      <p className="text-xs text-gray-400 mt-0.5">matched by code first</p>
                    </div>
                    <div className="border border-gray-200 rounded-lg px-4 py-3">
                      <p className="text-xs text-gray-400 mb-1">Name-only rows</p>
                      <p className="text-xl font-semibold text-gray-900">{preview.without_code}</p>
                      <p className="text-xs text-gray-400 mt-0.5">matched by name / alias</p>
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500">Sample (first 8 rows)</div>
                    <table className="w-full text-xs">
                      <thead className="border-t border-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Code</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Bank</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Account</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.sample.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-gray-500">{r.supplier_code || '—'}</td>
                            <td className="px-3 py-2 text-gray-700">{r.name}</td>
                            <td className="px-3 py-2 text-gray-500">{r.bank_name || '—'}</td>
                            <td className="px-3 py-2 text-gray-500">{r.bank_account || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-4 text-sm flex-wrap">
                    {Object.entries(preview.summary).map(([action, count]) => (
                      <span key={action} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-medium text-xs ${actionColor(action)}`}>
                        {count} {action}
                      </span>
                    ))}
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Action</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Code</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Matched by</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-500">Changes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.results.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${actionColor(r.action)}`}>{r.action}</span>
                            </td>
                            <td className="px-3 py-2 text-gray-700">{r.supplier_name || r.input?.name}</td>
                            <td className="px-3 py-2 text-gray-500 font-mono">{r.input?.supplier_code || '—'}</td>
                            <td className="px-3 py-2 text-gray-500">{r.matched_by || '—'}</td>
                            <td className="px-3 py-2 text-gray-500">{(r.changes || []).join(', ') || (r.reason || '—')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {importProgress && (
                <div className="space-y-1 pt-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Importing…</span>
                    <span>{importProgress.done} / {importProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.round(importProgress.done / importProgress.total * 100)}%` }} />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setStep('map')} disabled={importing} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50">Back</button>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {importing ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheck className="w-4 h-4" />}
                  Confirm Import
                </button>
              </div>
            </div>
          )}

          {/* Step: done */}
          {step === 'done' && importResult && (
            <div className="space-y-4 text-center py-4">
              <FiCheck className="w-10 h-10 text-green-600 mx-auto" />
              <p className="font-semibold text-gray-900">Import complete</p>
              <div className="flex justify-center gap-4 text-sm flex-wrap">
                {Object.entries(importResult.summary).map(([action, count]) => (
                  <span key={action} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-medium text-xs ${actionColor(action)}`}>
                    {count} {action}
                  </span>
                ))}
              </div>
              <button onClick={() => { onDone(); onClose(); }}
                className="mt-2 px-5 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────

function DeleteConfirmModal({ supplier, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-red-600">
            <FiTrash2 className="w-5 h-5" />
            <h2 className="text-base font-semibold">Remove Supplier</h2>
          </div>
          <button onClick={onCancel} disabled={deleting} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <p className="text-sm text-gray-700">
            Remove <span className="font-semibold">"{supplier.canonical_name}"</span> from the supplier registry?
          </p>
          <p className="text-xs text-gray-500">
            The supplier will be deactivated. Historical recon matches linked to this supplier are not deleted.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onCancel} disabled={deleting} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {deleting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />}
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SupplierCard ─────────────────────────────────────────────────────────────

function SupplierCard({ supplier, onEdit, onDelete, onAliasAdded, onAliasDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [addingAlias, setAddingAlias] = useState(false);
  const [newAlias, setNewAlias] = useState('');
  const [aliasLoading, setAliasLoading] = useState(false);
  const [aliasError, setAliasError] = useState(null);

  const aliases = supplier.supplier_aliases || [];
  const hasDetails = supplier.vat_number || supplier.bank_name || supplier.bank_account ||
    supplier.address || supplier.contact_email || supplier.contact_phone || supplier.notes;

  const handleAddAlias = async () => {
    const trimmed = newAlias.trim();
    if (!trimmed) return;
    setAliasLoading(true);
    setAliasError(null);
    try {
      await suppliersAPI.addAlias(supplier.id, trimmed);
      setNewAlias('');
      setAddingAlias(false);
      onAliasAdded(supplier.id);
    } catch (err) {
      setAliasError(err.response?.data?.error || 'Could not add alias');
    } finally {
      setAliasLoading(false);
    }
  };

  const handleDeleteAlias = async (aliasId) => {
    try {
      await suppliersAPI.deleteAlias(supplier.id, aliasId);
      onAliasDeleted(supplier.id);
    } catch { /* ignore */ }
  };

  return (
    <div className={`bg-white border rounded-xl shadow-sm transition-all ${supplier.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
      {/* Header row */}
      <div className="flex items-start justify-between px-5 py-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{supplier.canonical_name}</span>
            {!supplier.is_active && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Inactive</span>
            )}
            {supplier.match_count > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
                {supplier.match_count} recon {supplier.match_count === 1 ? 'match' : 'matches'}
              </span>
            )}
          </div>
          {supplier.group_name && (
            <p className="text-xs text-gray-400 mt-0.5">{supplier.group_name}</p>
          )}

          {/* Quick meta badges */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {supplier.supplier_code && (
              <span className="flex items-center gap-1 text-xs text-gray-500 font-mono">
                <FiHash className="w-3 h-3" /> {supplier.supplier_code}
              </span>
            )}
            {supplier.vat_number && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiHash className="w-3 h-3" /> VAT: {supplier.vat_number}
              </span>
            )}
            {supplier.bank_name && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiDollarSign className="w-3 h-3" /> {supplier.bank_name}{supplier.bank_account ? ` · ${supplier.bank_account}` : ''}
              </span>
            )}
            {supplier.contact_phone && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiPhone className="w-3 h-3" /> {supplier.contact_phone}
              </span>
            )}
            {supplier.contact_email && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiMail className="w-3 h-3" /> {supplier.contact_email}
              </span>
            )}
            {supplier.address && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <FiMapPin className="w-3 h-3" /> {supplier.address}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
          <button
            onClick={() => onEdit(supplier)}
            className="p-2 text-gray-400 hover:text-primary-600 transition-colors rounded-lg hover:bg-primary-50"
            title="Edit supplier"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(supplier)}
            className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
            title="Remove supplier"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
          {hasDetails && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-50"
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Aliases row */}
      <div className="px-5 pb-3 flex flex-wrap items-center gap-2 border-t border-gray-50 pt-3">
        <FiTag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        {aliases.length === 0 && !addingAlias && (
          <span className="text-xs text-gray-400 italic">No aliases</span>
        )}
        {aliases.map(a => (
          <span
            key={a.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs"
          >
            {a.alias}
            <button
              onClick={() => handleDeleteAlias(a.id)}
              className="text-gray-400 hover:text-red-500 transition-colors ml-0.5"
              title="Remove alias"
            >
              <FiX className="w-3 h-3" />
            </button>
          </span>
        ))}
        {addingAlias ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              type="text"
              value={newAlias}
              onChange={e => setNewAlias(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddAlias(); if (e.key === 'Escape') { setAddingAlias(false); setNewAlias(''); } }}
              placeholder="Alias name..."
              className="border border-gray-300 rounded px-2 py-0.5 text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-36"
            />
            <button
              onClick={handleAddAlias}
              disabled={aliasLoading}
              className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
            >
              {aliasLoading ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => { setAddingAlias(false); setNewAlias(''); setAliasError(null); }} className="p-1 text-gray-400 hover:text-gray-600">
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setAddingAlias(true)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-gray-300 text-gray-400 hover:text-primary-600 hover:border-primary-400 text-xs transition-colors"
          >
            <FiPlus className="w-3 h-3" /> Add alias
          </button>
        )}
        {aliasError && <span className="text-xs text-red-500">{aliasError}</span>}
      </div>

      {/* Expanded details */}
      {expanded && hasDetails && (
        <div className="px-5 pb-4 pt-1 border-t border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {supplier.bank_branch && (
              <div>
                <p className="text-xs text-gray-400">Branch Code</p>
                <p className="text-gray-700">{supplier.bank_branch}</p>
              </div>
            )}
            {supplier.notes && (
              <div className="sm:col-span-2">
                <p className="text-xs text-gray-400">Notes</p>
                <p className="text-gray-700 text-sm">{supplier.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Suppliers() {
  const [groups, setGroups] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deletingSupplier, setDeletingSupplier] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      const res = await supplierReconAPI.getGroups();
      setGroups(res.data?.groups || []);
    } catch { /* ignore */ }
  }, []);

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await suppliersAPI.list(selectedGroupId || undefined);
      setSuppliers(res.data?.suppliers || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const handleSave = () => {
    setShowAddModal(false);
    setEditingSupplier(null);
    loadSuppliers();
  };

  const handleDelete = async () => {
    if (!deletingSupplier) return;
    setDeleting(true);
    try {
      await suppliersAPI.delete(deletingSupplier.id);
      setDeletingSupplier(null);
      loadSuppliers();
    } finally {
      setDeleting(false);
    }
  };

  const refreshSupplier = useCallback(async (supplierId) => {
    try {
      const res = await suppliersAPI.get(supplierId);
      setSuppliers(prev => prev.map(s => s.id === supplierId ? { ...s, ...res.data.supplier } : s));
    } catch { /* ignore */ }
  }, []);

  const filtered = suppliers.filter(s => {
    if (!showInactive && !s.is_active) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.canonical_name?.toLowerCase().includes(q) ||
      s.supplier_code?.toLowerCase().includes(q) ||
      s.vat_number?.toLowerCase().includes(q) ||
      s.bank_name?.toLowerCase().includes(q) ||
      s.bank_account?.toLowerCase().includes(q) ||
      s.address?.toLowerCase().includes(q) ||
      (s.supplier_aliases || []).some(a => a.alias.includes(q))
    );
  });

  const activeCount = suppliers.filter(s => s.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <FiUsers className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Suppliers</h1>
              <p className="text-sm text-gray-500">
                Permanent supplier registry · {activeCount} active supplier{activeCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <FiUpload className="w-4 h-4" />
            Import CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
          >
            <FiPlus className="w-4 h-4" />
            Add Supplier
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search suppliers, aliases, bank..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select
          value={selectedGroupId}
          onChange={e => setSelectedGroupId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
        >
          <option value="">All Groups</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 text-primary-600"
          />
          Show inactive
        </label>
      </div>

      {/* Content */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <FiLoader className="w-6 h-6 animate-spin mr-2" />
          Loading suppliers...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FiUsers className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-gray-500">
            {suppliers.length === 0 ? 'No suppliers yet' : 'No suppliers match your search'}
          </p>
          {suppliers.length === 0 && (
            <p className="text-sm mt-1">
              Suppliers are captured automatically when you run a recon session and use{' '}
              <span className="font-medium">Capture to Suppliers</span>, or you can add them manually.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(supplier => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onEdit={setEditingSupplier}
              onDelete={setDeletingSupplier}
              onAliasAdded={refreshSupplier}
              onAliasDeleted={refreshSupplier}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {(showAddModal || editingSupplier) && (
        <SupplierModal
          supplier={editingSupplier}
          groups={groups}
          onSave={handleSave}
          onClose={() => { setShowAddModal(false); setEditingSupplier(null); }}
        />
      )}

      {deletingSupplier && (
        <DeleteConfirmModal
          supplier={deletingSupplier}
          onConfirm={handleDelete}
          onCancel={() => setDeletingSupplier(null)}
          deleting={deleting}
        />
      )}

      {showImportModal && (
        <ImportModal
          groups={groups}
          onClose={() => setShowImportModal(false)}
          onDone={loadSuppliers}
        />
      )}
    </div>
  );
}
